import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Shirt,
  Download,
  RotateCcw,
  Home,
  AlertTriangle,
  Loader2,
  Sparkles,
} from 'lucide-react';
import api from '../services/api';
import Navbar from '../components/Navbar';
import AccessModal from '../components/AccessModal';
import InsufficientTokensModal from '../components/InsufficientTokensModal';
import VipModal from '../components/VipModal';
import TokenBalanceCard from '../components/TokenBalanceCard';
import Button from '../components/ui/Button';
import { trackUploadImage, trackViewContent } from '../utils/metaPixel';

const Upload = () => {
  const { user, updateUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [personFile, setPersonFile] = useState(null);
  const [clothingFile, setClothingFile] = useState(null);
  const [personPreview, setPersonPreview] = useState(null);
  const [clothingPreview, setClothingPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showVipModal, setShowVipModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showInsufficientTokensModal, setShowInsufficientTokensModal] = useState(false);

  useEffect(() => {
    trackViewContent('upload_page', 'page', { content_name: 'Upload' });
  }, []);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-rose-500" />
          <p className="text-sm text-zinc-400">Carregando...</p>
        </div>
      </div>
    );
  }

  const handlePersonFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPersonFile(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setPersonPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleClothingFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setClothingFile(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setClothingPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowAccessModal(true);
      return;
    }
    if (!personFile) {
      setError('Selecione uma foto da pessoa');
      return;
    }
    if (!clothingFile) {
      setError('Selecione uma foto da roupa');
      return;
    }
    if (user.tokens < 25) {
      setShowInsufficientTokensModal(true);
      return;
    }

    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('personImage', personFile);
    formData.append('clothingImage', clothingFile);

    try {
      trackUploadImage();
      const response = await api.post('/upload/process-two-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
      trackViewContent('image_result', 'generated_content');
      updateUser({ tokens: response.data.tokensRemaining });
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao processar imagens');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPersonFile(null);
    setClothingFile(null);
    setPersonPreview(null);
    setClothingPreview(null);
    setResult(null);
    setError('');
  };

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      const imageUrl = result.processedImageUrl;
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resultado-virtual-tryon-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(result.processedImageUrl, '_blank');
    } finally {
      setDownloadLoading(false);
    }
  };

  const UploadSlot = ({ id, preview, file, onChange, icon: Icon, title, subtitle, accent }) => (
    <div>
      <div className="mb-3 text-center">
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <p className="text-sm text-zinc-400">{subtitle}</p>
      </div>
      <div
        className={`rounded-2xl border border-dashed bg-zinc-800/40 p-5 text-center transition-colors hover:border-opacity-80 ${accent}`}
      >
        <input type="file" accept="image/*" onChange={onChange} className="hidden" id={id} />
        <label htmlFor={id} className="flex cursor-pointer flex-col items-center gap-3">
          {preview ? (
            <div className="relative">
              <img
                src={preview}
                alt="Preview"
                className="h-32 w-32 rounded-xl object-cover ring-1 ring-white/10"
              />
              <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40 opacity-0 transition-opacity hover:opacity-100">
                <span className="text-xs font-medium text-white">Trocar</span>
              </div>
            </div>
          ) : (
            <div className="flex h-32 w-32 flex-col items-center justify-center rounded-xl border border-white/10 bg-white/5">
              <Icon className="mb-2 h-8 w-8 text-zinc-500" />
              <span className="text-xs text-zinc-500">Selecionar</span>
            </div>
          )}
          <div className="text-center">
            <span className="block text-sm font-medium text-white">
              {file ? file.name : 'Nenhuma imagem selecionada'}
            </span>
            <p className="mt-1 text-xs text-zinc-500">JPG, PNG, WebP (máx. 10MB)</p>
          </div>
        </label>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="mx-auto max-w-md px-4 pb-10 pt-20">
        {!user && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-200">Login necessário</p>
              <p className="mt-0.5 text-xs text-amber-200/70">
                Faça login e compre tokens para gerar imagens com IA
              </p>
            </div>
          </div>
        )}

        <TokenBalanceCard
          tokens={user?.tokens || 0}
          isVip={user?.isVip}
          onBuy={() => navigate('/tokens')}
          onVip={() => setShowVipModal(true)}
        />

        <div className="rounded-2xl border border-white/10 bg-zinc-900 p-6">
          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <UploadSlot
                id="person-upload"
                preview={personPreview}
                file={personFile}
                onChange={handlePersonFileSelect}
                icon={User}
                title="Sua foto"
                subtitle="Foto clara da pessoa"
                accent="border-rose-500/40 hover:border-rose-500"
              />
              <UploadSlot
                id="clothing-upload"
                preview={clothingPreview}
                file={clothingFile}
                onChange={handleClothingFileSelect}
                icon={Shirt}
                title="Roupa"
                subtitle="Peça que deseja experimentar"
                accent="border-zinc-600 hover:border-zinc-400"
              />

              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={loading}
                disabled={!personFile || !clothingFile}
                icon={Sparkles}
              >
                {loading ? 'Processando com IA...' : 'Experimentar agora'}
              </Button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-center text-sm text-emerald-300">
                {result.message}
              </div>

              <div>
                <h3 className="mb-3 text-center text-base font-semibold text-white">Resultado</h3>
                <div className="overflow-hidden rounded-xl border border-white/10 bg-zinc-800/50 p-3">
                  <img
                    src={result.processedImageUrl}
                    alt="Resultado"
                    className="mb-3 w-full rounded-lg"
                  />
                  <Button
                    onClick={handleDownload}
                    fullWidth
                    loading={downloadLoading}
                    icon={Download}
                    variant="secondary"
                  >
                    {downloadLoading ? 'Baixando...' : 'Baixar imagem'}
                  </Button>
                </div>
              </div>

              <div className="space-y-2.5">
                <Button onClick={resetForm} fullWidth icon={RotateCcw}>
                  Experimentar outra roupa
                </Button>
                <Button onClick={() => navigate('/app')} fullWidth variant="secondary" icon={Home}>
                  Voltar ao início
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <VipModal isOpen={showVipModal} onClose={() => setShowVipModal(false)} />
      <AccessModal
        isOpen={showAccessModal}
        onClose={() => setShowAccessModal(false)}
        title="Acesso necessário"
        message="Você precisa fazer login para gerar imagens com IA"
        redirectTo="/upload"
      />
      <InsufficientTokensModal
        isOpen={showInsufficientTokensModal}
        onClose={() => setShowInsufficientTokensModal(false)}
        requiredTokens={25}
        currentTokens={user?.tokens || 0}
      />
    </div>
  );
};

export default Upload;
